import { useNoSleep } from '@/hooks/NoSleep';
import { useSubmitDrawing } from '@/hooks/PlayerSessionControlHooks';
import { useRef, useState } from 'react';
import AnimatedPlayerSharing from '../Animated/Backgrounds/AnimatedPlayerSharingBackground';
import Canvas, { LineProps } from '../Controls/Canvas';
import PlayerPrompt from './PlayerPrompt';

/**
 * @param promptText Optional prompt text. When set, the prompt is rendered directly
 * instead of being fetched, which lets demo mode run with no API access.
 */
export default function PlayerCanvas({ promptText }: { promptText?: string } = {}) {
  // State
  const [drawing, setDrawing] = useState<LineProps[]>([]);
  const submitRef = useRef(false);

  // Hooks
  const { enableNoSleep } = useNoSleep();

  const submitDrawing = useSubmitDrawing();

  const onSubmit = async () => {
    if (submitRef.current) return;
    submitRef.current = true;

    enableNoSleep();

    try {
      await submitDrawing(JSON.stringify(drawing));
      setDrawing([]);
    } catch {
      // The provider retains a user-facing transport error. Keep the drawing intact so
      // the player can press Submit again instead of losing their work.
    } finally {
      // Transport failures must not permanently lock the Submit button.
      submitRef.current = false;
    }
  };

  return (
    <div className="fixed inset-0 min-h-screen min-w-screen h-full w-full flex items-center justify-center">
      <AnimatedPlayerSharing />

      <div className="relative max-w-lg w-full h-full">
        <PlayerPrompt text={promptText} />
        <div className="flex flex-col items-center justify-center">
          <div className="w-full h-[100% - 20rem] max-w-lg">
            <Canvas
              drawing={drawing}
              setDrawing={setDrawing}
              onSubmit={onSubmit}
              submissionEnabled={!submitRef.current}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
